import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { FeatureFlag, FeatureFlagCreatePayload, FeatureFlagUpdatePayload } from '../../../models/featureFlag';

export interface FeatureFlagFormModalProps {
  visible: boolean;
  mode: 'create' | 'edit';
  /** Existing flag when editing. */
  initial?: FeatureFlag | null;
  onClose: () => void;
  onCreate: (payload: FeatureFlagCreatePayload) => Promise<void>;
  onUpdate: (id: string, payload: FeatureFlagUpdatePayload) => Promise<void>;
}

interface FormState {
  key: string;
  description: string;
  enabledGlobally: boolean;
}

function buildInitialState(mode: 'create' | 'edit', initial?: FeatureFlag | null): FormState {
  if (mode === 'edit' && initial) {
    return {
      key: initial.key,
      description: initial.description ?? '',
      enabledGlobally: initial.enabledGlobally,
    };
  }
  return { key: '', description: '', enabledGlobally: false };
}

export function FeatureFlagFormModal({
  visible,
  mode,
  initial,
  onClose,
  onCreate,
  onUpdate,
}: FeatureFlagFormModalProps): React.ReactElement | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const [form, setForm] = useState<FormState>(() => buildInitialState(mode, initial));
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (visible) {
      setForm(buildInitialState(mode, initial));
      setFieldErrors({});
      setError(null);
      setPending(false);
    }
  }, [visible, mode, initial]);

  const title = useMemo(() => (mode === 'create' ? 'New feature flag' : 'Edit feature flag'), [mode]);

  if (!visible) {
    return null;
  }

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (mode === 'create' && !form.key.trim()) {
      next.key = 'A key is required.';
    }
    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    if (!validate()) {
      return;
    }
    setPending(true);
    try {
      const description = form.description.trim();
      if (mode === 'create') {
        await onCreate({
          key: form.key.trim(),
          description: description || null,
          enabledGlobally: form.enabledGlobally,
        });
      } else if (initial) {
        await onUpdate(initial.id, {
          description,
          enabledGlobally: form.enabledGlobally,
        });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the feature flag.');
    } finally {
      setPending(false);
    }
  };

  return (
    <div
      className="bo-modal-backdrop"
      role="presentation"
      onClick={() => {
        if (!pending) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        className="bo-modal-panel"
        role="dialog"
        aria-modal
        aria-labelledby="bo-feature-flag-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="bo-feature-flag-modal-title" className="bo-modal-title">
          {title}
        </h2>
        <form className="bo-form-fields" onSubmit={(e) => void handleSubmit(e)}>
          <label className="bo-field" htmlFor="feature-flag-key">
            <span className="bo-field-label">Key</span>
            <input
              id="feature-flag-key"
              className="bo-input"
              value={form.key}
              disabled={mode === 'edit'}
              onChange={(e) => setField('key', e.target.value)}
              placeholder="e.g. tiktok_sync"
            />
            {fieldErrors.key ? (
              <span className="bo-field-error" role="alert">
                {fieldErrors.key}
              </span>
            ) : null}
          </label>
          {mode === 'create' ? (
            <p className="bo-muted" style={{ marginTop: '-0.5rem' }}>
              Normalized to lowercase snake_case on save. Cannot be changed later.
            </p>
          ) : null}

          <label className="bo-field" htmlFor="feature-flag-description">
            <span className="bo-field-label">Description</span>
            <textarea
              id="feature-flag-description"
              className="bo-input"
              rows={3}
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              placeholder="What does this flag control?"
            />
          </label>

          <label className="bo-field bo-field-checkbox" htmlFor="feature-flag-enabled">
            <input
              id="feature-flag-enabled"
              type="checkbox"
              checked={form.enabledGlobally}
              onChange={(e) => setField('enabledGlobally', e.target.checked)}
            />
            <span className="bo-field-label">Enabled globally (default for everyone)</span>
          </label>
          <p className="bo-muted" style={{ marginTop: '-0.5rem' }}>
            Unchecking this does not disable existing company, user, or role assignments — the flag stays active
            only for those specific assignees. Manage assignments from the flag&rsquo;s row in the list; use
            Archive to fully disable a flag for everyone.
          </p>

          {error ? (
            <p className="bo-modal-error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="bo-actions" style={{ marginTop: '1.25rem', justifyContent: 'flex-end' }}>
            <button type="button" className="bo-btn bo-btn-secondary" onClick={onClose} disabled={pending}>
              Cancel
            </button>
            <button type="submit" className="bo-btn bo-btn-primary" disabled={pending}>
              {pending ? 'Saving…' : mode === 'create' ? 'Create flag' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
